import { fileUpload } from 'utils/storageUtils';
import { client } from 'graphql/client';
import {
  PROCESS_TRACK_EXPORT_FILE,
  type ProcessTrackExportFileResponse,
} from '../graphql/mutations/processTrackExportFile';
import {
  GET_TRACK_EXPORT_TASK_LIST,
  GET_TRACK_EXPORT_TASK_DETAIL,
  GET_TRACK_EXPORT_INTERNAL_DOWNLOAD_URL,
  GET_TRACK_EXPORT_EXTERNAL_DOWNLOAD_URL,
  GET_TRACK_EXPORT_UPLOAD_TEMPLATE_BASE64,
} from '../graphql/queries/trackExportQueries';

export interface TrackExportTask {
  id: number;
  taskNo: string;
  fileName: string;
  operatorId: number;
  operatorName: string | null;
  status: number;
  internalS3Key: string | null;
  externalS3Key: string | null;
  errorDetailJson: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface TrackExportUploadResponse {
  taskId: number;
  taskNo: string;
  status: number;
  fileName: string;
  message: string;
}

export interface TrackExportErrorDetail {
  reason: string;
  invalidRows?: number[];
  sample?: string;
  message?: string;
}

export interface TrackExportTaskListResponse {
  content: TrackExportTask[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

function mapTaskRow(row: Record<string, unknown>): TrackExportTask {
  return {
    id: Number(row.id),
    taskNo: String(row.taskNo),
    fileName: String(row.fileName),
    operatorId: Number(row.operatorId),
    operatorName: row.operatorName != null ? String(row.operatorName) : null,
    status: Number(row.status),
    internalS3Key: row.internalS3Key != null ? String(row.internalS3Key) : null,
    externalS3Key: row.externalS3Key != null ? String(row.externalS3Key) : null,
    errorDetailJson: row.errorDetailJson != null ? String(row.errorDetailJson) : null,
    createdAt: row.createdAt != null ? String(row.createdAt) : '',
    updatedAt: row.updatedAt != null ? String(row.updatedAt) : null,
  };
}

/**
 * 上传轨迹导出文件（GraphQL mutation + fileUpload 到 S3）
 */
export async function uploadFile(file: File): Promise<TrackExportUploadResponse> {
  const { objectKey } = await fileUpload(file, 'track-export');

  const { data, errors } = await client.mutate<ProcessTrackExportFileResponse>({
    mutation: PROCESS_TRACK_EXPORT_FILE,
    variables: {
      input: {
        objectKey,
        fileName: file.name,
      },
    },
  });

  if (errors?.length) {
    throw new Error(errors[0]?.message ?? '上传失败');
  }

  if (!data?.processTrackExportFile) {
    throw new Error('上传失败');
  }

  return data.processTrackExportFile;
}

export async function getTaskList(params: {
  page?: number;
  size?: number;
  status?: number;
  operatorId?: number;
}): Promise<TrackExportTaskListResponse> {
  const { data, errors } = await client.query({
    query: GET_TRACK_EXPORT_TASK_LIST,
    variables: {
      page: params.page ?? 1,
      size: params.size ?? 20,
      status: params.status ?? null,
      operatorId: params.operatorId ?? null,
    },
    fetchPolicy: 'network-only',
  });

  if (errors?.length) {
    throw new Error(errors[0]?.message ?? '获取任务列表失败');
  }

  const raw = (data as { getTrackExportTaskList?: Record<string, unknown> })?.getTrackExportTaskList;
  if (!raw || !Array.isArray(raw.content)) {
    throw new Error('获取任务列表失败');
  }

  const content = (raw.content as Record<string, unknown>[]).map(mapTaskRow);

  return {
    content,
    page: Number(raw.page),
    size: Number(raw.size),
    totalElements: Number(raw.totalElements),
    totalPages: Number(raw.totalPages),
    hasNext: Boolean(raw.hasNext),
    hasPrevious: Boolean(raw.hasPrevious),
  };
}

export async function getTaskById(id: number): Promise<TrackExportTask | null> {
  const { data, errors } = await client.query({
    query: GET_TRACK_EXPORT_TASK_DETAIL,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  if (errors?.length) {
    throw new Error(errors[0]?.message ?? '获取任务详情失败');
  }

  const row = (data as { getTrackExportTaskDetail?: Record<string, unknown> | null })
    ?.getTrackExportTaskDetail;
  if (!row) {
    return null;
  }
  return mapTaskRow(row);
}

export async function getInternalDownloadUrl(taskId: number): Promise<string> {
  const { data, errors } = await client.query({
    query: GET_TRACK_EXPORT_INTERNAL_DOWNLOAD_URL,
    variables: { taskId },
    fetchPolicy: 'network-only',
  });

  if (errors?.length) {
    const m = errors[0]?.message ?? '';
    if (m.includes('permission') || m.includes('权限')) throw new Error('无权限下载');
    throw new Error(m || '获取下载链接失败');
  }

  const url = (data as { getTrackExportInternalDownloadUrl?: string | null })
    ?.getTrackExportInternalDownloadUrl;
  if (!url || typeof url !== 'string') {
    throw new Error('任务不存在或文件未生成');
  }
  return url;
}

export async function getExternalDownloadUrl(taskId: number): Promise<string> {
  const { data, errors } = await client.query({
    query: GET_TRACK_EXPORT_EXTERNAL_DOWNLOAD_URL,
    variables: { taskId },
    fetchPolicy: 'network-only',
  });

  if (errors?.length) {
    const m = errors[0]?.message ?? '';
    if (m.includes('permission') || m.includes('权限')) throw new Error('无权限下载');
    throw new Error(m || '获取下载链接失败');
  }

  const url = (data as { getTrackExportExternalDownloadUrl?: string | null })
    ?.getTrackExportExternalDownloadUrl;
  if (!url || typeof url !== 'string') {
    throw new Error('任务不存在或文件未生成');
  }
  return url;
}

export async function downloadTemplate(): Promise<void> {
  const { data, errors } = await client.query({
    query: GET_TRACK_EXPORT_UPLOAD_TEMPLATE_BASE64,
    fetchPolicy: 'network-only',
  });

  if (errors?.length) {
    throw new Error(errors[0]?.message ?? '下载模板失败');
  }

  const b64 = (data as { getTrackExportUploadTemplateBase64?: string })?.getTrackExportUploadTemplateBase64;
  if (!b64 || typeof b64 !== 'string') {
    throw new Error('下载模板失败');
  }

  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = '模版_轨迹.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
